import { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, X, ArrowUpRight } from "lucide-react";
import SectionHead from "@/components/SectionHead";
import { iWords, categories } from "@/data/content";

const IWord = ({ w, i }) => (
  <motion.div
    data-testid={`iword-${w.word.toLowerCase()}`}
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-40px" }}
    transition={{ duration: 0.7, delay: (i % 3) * 0.12 }}
    className="border-b border-white/5 py-6 sm:py-8"
  >
    <span
      className="text-4xl sm:text-5xl lg:text-6xl text-slate-200"
      style={{ fontFamily: w.font, fontWeight: w.weight, fontStyle: w.style, letterSpacing: w.tracking }}
    >
      <span className="text-gradient">{w.word[0]}</span>
      {w.word.slice(1)}
    </span>
  </motion.div>
);

const spans = [
  "lg:col-span-4 lg:row-span-2 min-h-[280px] lg:min-h-[460px]",
  "lg:col-span-2 min-h-[220px]",
  "lg:col-span-2 min-h-[220px]",
  "lg:col-span-2 min-h-[220px]",
  "lg:col-span-2 min-h-[220px]",
];

const CategoryCard = ({ c, i, onOpen }) => {
  const vidRef = useRef(null);
  const enter = () => vidRef.current?.play().catch(() => {});
  const leave = () => {
    if (vidRef.current) {
      vidRef.current.pause();
      vidRef.current.currentTime = 0;
    }
  };
  const cover = c.videos[0];
  return (
    <motion.button
      data-testid={`role-category-tab-${c.id}`}
      onClick={() => onOpen(c)}
      onMouseEnter={enter}
      onMouseLeave={leave}
      initial={{ opacity: 0, y: 32 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.7, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] }}
      className={`group relative overflow-hidden rounded-2xl border border-white/10 bg-elevated text-left transition-all duration-500 hover:border-glow/60 hover:shadow-[0_0_60px_-14px_rgba(0,245,212,0.5)] ${spans[i]}`}
    >
      <video
        ref={vidRef}
        src={cover.videoUrl}
        poster={cover.thumb}
        muted
        loop
        playsInline
        preload="metadata"
        className="absolute inset-0 h-full w-full object-cover opacity-70 transition-all duration-700 group-hover:opacity-100 group-hover:scale-[1.05]"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-ink/95 via-ink/30 to-ink/10" />
      <span className="absolute top-4 right-4 flex h-9 w-9 items-center justify-center rounded-full border border-white/20 bg-ink/50 text-slate-200 backdrop-blur transition-all duration-300 group-hover:bg-glow group-hover:text-ink group-hover:border-glow">
        <ArrowUpRight size={16} />
      </span>
      <span className="absolute top-4 left-4 rounded-full bg-ink/60 backdrop-blur px-3 py-1 text-[10px] uppercase tracking-[0.2em] text-turq border border-turq/30">
        {c.videos.length} video
      </span>
      <div className="absolute bottom-0 left-0 right-0 p-5 sm:p-6">
        <p className="font-syne text-xl sm:text-2xl lg:text-3xl font-bold text-slate-50">{c.label}</p>
        <p className="mt-1 text-[11px] uppercase tracking-[0.25em] text-slate-400 opacity-0 translate-y-2 transition-all duration-300 group-hover:opacity-100 group-hover:translate-y-0">
          Seçkiyi aç
        </p>
      </div>
    </motion.button>
  );
};

const CategoryModal = ({ cat, onClose, onPlay }) => (
  <motion.div
    data-testid="category-modal"
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    className="fixed inset-0 z-[70] overflow-y-auto bg-ink/95 backdrop-blur-md p-4 sm:p-8"
    onClick={onClose}
  >
    <motion.div
      initial={{ y: 40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: 24, opacity: 0 }}
      transition={{ type: "spring", stiffness: 220, damping: 26 }}
      className="max-w-6xl mx-auto pt-16 pb-10"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-start justify-between mb-10">
        <div>
          <p className="text-xs uppercase tracking-[0.4em] text-turq mb-3">Video Seçkisi</p>
          <h3 className="font-syne text-3xl sm:text-5xl font-bold">{cat.label}</h3>
        </div>
        <button
          data-testid="category-modal-close-button"
          onClick={onClose}
          className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-slate-300 hover:text-glow transition-colors mt-2"
        >
          Kapat <X size={20} />
        </button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {cat.videos.map((v, i) => (
          <motion.button
            key={v.id}
            data-testid={`category-video-${v.id}`}
            onClick={() => onPlay(v)}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.08 * i, duration: 0.5 }}
            whileHover={{ y: -5 }}
            className={`group relative text-left ${v.ratio === "9/16" ? "max-w-[240px]" : ""}`}
          >
            <div
              className="relative w-full overflow-hidden rounded-lg border border-white/10 bg-elevated group-hover:border-turq/50 transition-colors"
              style={{ aspectRatio: v.ratio }}
            >
              <img
                src={v.thumb}
                alt={v.title}
                loading="lazy"
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/90 via-ink/10 to-transparent" />
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-glow/90 text-ink transition-transform group-hover:scale-110">
                  <Play size={16} fill="currentColor" />
                </span>
              </div>
              <p className="absolute bottom-0 left-0 right-0 p-3 text-xs sm:text-sm font-medium text-slate-100">{v.title}</p>
            </div>
          </motion.button>
        ))}
      </div>
    </motion.div>
  </motion.div>
);

export default function Role({ onPlay }) {
  const [activeCat, setActiveCat] = useState(null);

  return (
    <section id="role" data-testid="role-section" className="relative py-28 sm:py-36 bg-navy/30">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <SectionHead eyebrow="Our Role" title={<>We Create a World of <span className="text-gradient">Branded Content</span></>} testId="role-title" />

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-10">
          {iWords.map((w, i) => (
            <IWord key={w.word} w={w} i={i} />
          ))}
        </div>

        <div className="mt-24">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="flex items-end justify-between mb-8"
          >
            <p className="text-xs uppercase tracking-[0.35em] text-slate-400">
              Proje Kategorileri — seçkiyi açmak için tıklayın
            </p>
            <p className="hidden sm:block text-xs text-turq">05 koleksiyon</p>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-5">
            {categories.map((c, i) => (
              <CategoryCard key={c.id} c={c} i={i} onOpen={setActiveCat} />
            ))}
          </div>
        </div>
      </div>

      <AnimatePresence>
        {activeCat && <CategoryModal cat={activeCat} onClose={() => setActiveCat(null)} onPlay={onPlay} />}
      </AnimatePresence>
    </section>
  );
}
